import React, { useEffect, useState } from "react";
import { Grid, TextField, Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { getUserAuth } from "../../recoil/selectors";
import { patientProfileState } from "../../recoil/atoms";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  useModalAnalytics,
  ModalStep,
} from "../MissingBillingInformation/hooks/useModalAnalytics";
import { Banner } from "../Banner/Banner";
import { Button } from "../shared/Button/Button";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { statesArray } from "../Profile/helpers";
import {
  FormatedAddress,
  GetGooglePlacesAddress,
  GetGooglePlacesAddressDetails,
  BillingAddressUpdate,
  UpdateBillingAddress,
} from "../Profile/Services/UpdateProfileInfoService";
import { PatientInformationWrapper } from "../Profile/Profile.styles";
import { FormSection } from "./AddressForm.styles";
import {
  ProfileModalWrapper,
  ProfileModalTitle,
  ProfileModalContentWrapper,
  ProfileModalTitleWrapper,
  ProfileModalButtons,
} from "../shared/Modal/Modal.styles";

export interface BillingAddressFormData {
  streetAddress: string;
  aptUnit: string;
  city: string;
  state: string;
  zipCode: string;
}

interface AddressFormProps {
  onComplete: () => void;
  onCancel: () => void;
}

export const AddressForm: React.FC<AddressFormProps> = ({
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const authInfo = useRecoilValue(getUserAuth);
  const [patientInfo, setPatientInfo] = useRecoilState(patientProfileState);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Initialize form data from patient info
  const initialData = {
    streetAddress: patientInfo?.phi?.street_address_1 || "",
    aptUnit: patientInfo?.phi?.street_address_2 || "",
    city: patientInfo?.phi?.city || "",
    state: patientInfo?.phi?.state || "",
    zipCode: patientInfo?.phi?.zip || "",
  };

  const [formData, setFormData] = useState<BillingAddressFormData>(initialData);

  // Analytics hook
  const { trackInteraction } = useModalAnalytics(
    true,
    ModalStep.Address,
    false,
  );
  const [autocompleteAddressList, setAutocompleteAddressList] = useState<
    FormatedAddress[]
  >([]);
  const [selectedAutoCompleteAddress, setSelectedAutoCompleteAddress] =
    useState<FormatedAddress>();
  const [showValidation, setShowValidation] = useState(false);

  // Handle address autocomplete
  useEffect(() => {
    const getAutocompleteAddresses = async () => {
      if (formData.streetAddress.length > 3 && authInfo) {
        try {
          const addresses = await GetGooglePlacesAddress(
            formData.streetAddress,
            authInfo.access_token,
          );
          setAutocompleteAddressList(addresses);
        } catch (err) {
          setAutocompleteAddressList([]);
        }
      }
    };

    getAutocompleteAddresses();
  }, [formData.streetAddress, authInfo]);

  // Handle selected autocomplete address details
  useEffect(() => {
    const getAutocompleteAddressesDetails = async () => {
      if (selectedAutoCompleteAddress && authInfo) {
        try {
          const details = await GetGooglePlacesAddressDetails(
            selectedAutoCompleteAddress,
            authInfo.access_token,
          );

          if (details) {
            setFormData({
              streetAddress:
                `${details.houseNumber} ${details.streetName}` || "",
              aptUnit: formData.aptUnit,
              city: details.city || "",
              state:
                statesArray.find((state) =>
                  state.split("-")[0].includes(details.state),
                ) || details.state,
              zipCode: details.zip || "",
            });
            setSelectedAutoCompleteAddress(undefined);
            setAutocompleteAddressList([]);
          }
        } catch (err) {
          setSelectedAutoCompleteAddress(undefined);
        }
      }
    };
    getAutocompleteAddressesDetails();
  }, [selectedAutoCompleteAddress, authInfo]);

  const isFormValid = () =>
    formData.streetAddress.trim() &&
    formData.city.trim() &&
    formData.state.trim() &&
    formData.zipCode.trim();

  // Helper function to extract state code from statesArray format
  const getStateCode = (stateValue: string): string => {
    // Check if the state value is in the statesArray format "CODE - Name"
    const stateMatch = statesArray.find((state) => state === stateValue);
    if (stateMatch) {
      // Extract just the state code (everything before the first " - ")
      return stateMatch.split(" - ")[0];
    }
    // If not found in statesArray, return the original value
    return stateValue;
  };

  const handleSubmit = async () => {
    setShowValidation(true);
    if (!isFormValid()) return;

    if (!authInfo) {
      setError(t("address.errors.authenticationRequired"));
      return;
    }

    setLoading(true);
    trackInteraction("Billing Address Update", "Update");

    try {
      const addressData: BillingAddressUpdate = {
        street_address_1: formData.streetAddress,
        street_address_2: formData.aptUnit || undefined,
        city: formData.city,
        state: getStateCode(formData.state),
        zip: formData.zipCode,
      };

      const response = await UpdateBillingAddress(
        addressData,
        authInfo.access_token,
      );

      if (response.ok) {
        const updatedProfile = await response.json();
        setPatientInfo(updatedProfile);
        setError(null);
        onComplete();
      } else {
        setError(t("address.errors.updateFailed"));
      }
    } catch (err) {
      setError(t("address.errors.updateFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProfileModalWrapper deviceType={deviceType}>
      <ProfileModalTitle deviceType={deviceType}>
        <ProfileModalTitleWrapper deviceType={deviceType}>
          {t("address.title").toLocaleUpperCase()}
          <CloseIcon onClick={onCancel} />
        </ProfileModalTitleWrapper>
      </ProfileModalTitle>
      {loading ? (
        <ProfileModalContentWrapper>
          <SpinnerLoading />
        </ProfileModalContentWrapper>
      ) : (
        <>
          <ProfileModalContentWrapper>
            {error && (
              <Banner
                bannerType="warning"
                bannerTitle={t("address.errors.title")}
                bannerText={error}
              />
            )}
            <PatientInformationWrapper>
              <FormSection container spacing={3} columns={12}>
                <Grid item xs={12}>
                  <Autocomplete
                    freeSolo
                    options={autocompleteAddressList.map(
                      (address) => address.fullAddress,
                    )}
                    value={formData.streetAddress}
                    onChange={(event, newValue) => {
                      const selectedAddress = autocompleteAddressList.find(
                        (address) => address.fullAddress === newValue,
                      );
                      if (selectedAddress) {
                        setSelectedAutoCompleteAddress(selectedAddress);
                      } else {
                        setFormData({
                          ...formData,
                          streetAddress: newValue || "",
                        });
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFormData({
                        ...formData,
                        streetAddress: newInputValue,
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("address.fields.streetAddress")}
                        placeholder={t("address.placeholders.streetAddress")}
                        variant="outlined"
                        fullWidth
                        error={showValidation && !formData.streetAddress.trim()}
                        helperText={
                          showValidation && !formData.streetAddress.trim()
                            ? t("address.errors.streetAddressRequired")
                            : " "
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    id="billing-address-apt-suite"
                    label={t("address.fields.aptUnit")}
                    variant="outlined"
                    placeholder={t("address.fields.aptUnit")}
                    fullWidth
                    value={formData.aptUnit}
                    onChange={(e) =>
                      setFormData({ ...formData, aptUnit: e.target.value })
                    }
                    helperText=" "
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </FormSection>

              <FormSection container spacing={3} columns={12}>
                <Grid item xs={12}>
                  <TextField
                    id="billing-address-city"
                    label={t("address.fields.city")}
                    variant="outlined"
                    placeholder={t("address.placeholders.typeHere")}
                    fullWidth
                    value={formData.city}
                    error={showValidation && !formData.city.trim()}
                    helperText={
                      showValidation && !formData.city.trim()
                        ? t("address.errors.cityRequired")
                        : " "
                    }
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    options={statesArray}
                    value={formData.state}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, state: newValue || "" });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("address.fields.state")}
                        variant="outlined"
                        fullWidth
                        error={showValidation && !formData.state.trim()}
                        helperText={
                          showValidation && !formData.state.trim()
                            ? t("address.errors.stateRequired")
                            : " "
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    id="billing-address-zip"
                    label={t("address.fields.zipCode")}
                    variant="outlined"
                    placeholder={t("address.placeholders.typeHere")}
                    fullWidth
                    value={formData.zipCode}
                    error={showValidation && !formData.zipCode.trim()}
                    helperText={
                      showValidation && !formData.zipCode.trim()
                        ? t("address.errors.zipCodeRequired")
                        : " "
                    }
                    onChange={(e) =>
                      setFormData({ ...formData, zipCode: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </FormSection>
            </PatientInformationWrapper>
          </ProfileModalContentWrapper>
          <ProfileModalTitle deviceType={deviceType}>
            <ProfileModalButtons deviceType={deviceType}>
              <Button
                className="billing-address-cancel-button"
                label={t("address.actions.cancel")}
                data-testid="cancel-button"
                onClick={onCancel}
                size="medium"
                reverse
                backgroundColor="greenDark"
                disabled={loading}
              />
              <Button
                className="billing-address-update-button"
                label={t("address.actions.update")}
                data-testid="update-button"
                disabled={!isFormValid() || loading}
                onClick={handleSubmit}
                size="medium"
                backgroundColor="greenDark"
              />
            </ProfileModalButtons>
          </ProfileModalTitle>
        </>
      )}
    </ProfileModalWrapper>
  );
};
